import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash, PencilSimple, Plus } from "@phosphor-icons/react";

const DEFAULT_CATEGORIES = ["Learning", "Wellness", "Mentorship", "Swag", "Recognition"];

export default function RewardCatalogAdmin() {
  const [items, setItems] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const empty = { name: "", description: "", cost_credits: 500, category: "Learning", stock: -1, active: true };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [i, r] = await Promise.all([api.get("/admin/rewards"), api.get("/admin/rewards/redemptions")]);
    setItems(i.data); setRedemptions(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/admin/rewards/${editing.item_id}`, form);
        toast.success("Reward updated");
      } else {
        await api.post("/admin/rewards", form);
        toast.success("Reward added");
      }
      setShowForm(false); setEditing(null); setForm(empty);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reward?")) return;
    await api.delete(`/admin/rewards/${id}`);
    toast.success("Deleted"); load();
  };

  const fulfill = async (id) => {
    await api.patch(`/admin/rewards/redemptions/${id}`, { status: "fulfilled" });
    toast.success("Marked fulfilled"); load();
  };

  const startEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setShowForm(true);
  };

  return (
    <div className="space-y-4" data-testid="admin-rewards-page">
      <Tabs defaultValue="catalog" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="catalog" data-testid="admin-rewards-tab-catalog">Catalog</TabsTrigger>
          <TabsTrigger value="redemptions" data-testid="admin-rewards-tab-redemptions">Redemptions</TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="pt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }} className="rounded-sm bg-foreground text-background" data-testid="add-reward-btn">
              <Plus size={14} className="mr-1" /> Add reward
            </Button>
          </div>
          <div className="border border-border bg-background overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3 hidden md:table-cell">Category</th>
                  <th className="text-right py-2 px-3">Credits</th>
                  <th className="text-right py-2 px-3 hidden md:table-cell">Stock</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-right py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.item_id} className="border-b border-border">
                    <td className="py-2 px-3 font-medium">{i.name}</td>
                    <td className="py-2 px-3 hidden md:table-cell text-xs uppercase tracking-wider text-zinc-500">{i.category}</td>
                    <td className="py-2 px-3 text-right font-mono text-[#E1252A]">{i.cost_credits}</td>
                    <td className="py-2 px-3 text-right font-mono hidden md:table-cell">{i.stock === -1 ? "∞" : i.stock}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className="rounded-sm text-[10px] uppercase">{i.active ? "active" : "inactive"}</Badge></td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => startEdit(i)} className="rounded-sm h-7 px-2"><PencilSimple size={12} /></Button>
                        <Button size="sm" variant="outline" onClick={() => remove(i.item_id)} className="rounded-sm h-7 px-2 text-red-600 border-red-200"><Trash size={12} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-sm text-zinc-500">No rewards yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="redemptions" className="pt-4">
          <div className="border border-border bg-background overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="text-left py-2 px-3">When</th>
                  <th className="text-left py-2 px-3">User</th>
                  <th className="text-left py-2 px-3">Item</th>
                  <th className="text-right py-2 px-3">Credits</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-right py-2 px-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((r) => (
                  <tr key={r.redemption_id} className="border-b border-border">
                    <td className="py-2 px-3 text-xs text-zinc-500">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3">{r.user_name}</td>
                    <td className="py-2 px-3">{r.item_name}</td>
                    <td className="py-2 px-3 text-right font-mono text-[#E1252A]">{r.cost_credits}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className="rounded-sm text-[10px] uppercase">{r.status}</Badge></td>
                    <td className="py-2 px-3 text-right">
                      {r.status === "pending" && (
                        <Button size="sm" variant="outline" onClick={() => fulfill(r.redemption_id)} className="rounded-sm h-7">Fulfill</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {redemptions.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-sm text-zinc-500">No redemptions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setEditing(null); setForm(empty); } }}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit reward" : "New reward"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="reward-name-input" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cost (credits)</Label><Input type="number" min={0} value={form.cost_credits} onChange={(e) => setForm({ ...form, cost_credits: Number(e.target.value) })} className="rounded-sm" data-testid="reward-cost-input" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{DEFAULT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Stock (−1 for unlimited)</Label><Input type="number" min={-1} value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} className="rounded-sm" /></div>
              <div className="flex items-center justify-between border border-border p-2 rounded-sm">
                <span className="text-sm">Active</span>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-sm bg-foreground text-background" data-testid="reward-save-btn">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
